// src/lib/contractSeats.js — several signers may hold one role (owner ruling 2026-09-24: co-owners each sign
// as an owner). The server's twin is server-new-libs/lib/contract_seats.js. A party carries `seat` (1, 2 …);
// seat 1 keeps the plain role everywhere a key or anchor is built, so every existing contract, error anchor
// and saved draft reads exactly as before, and seat 2+ reads `owner#2`.
// ⚠️ A seat number is never reused or renumbered: the server soft-removes a dropped seat and keeps its number,
//    and a saved contract is matched seat by seat (or by row id). Only a party that has no seat yet gets one.
// ⚠️ The agency and the co-broker are one seat each — the server refuses a second of either.

export const SINGLE_SEAT_ROLES = new Set(['agency', 'cobroker'])
// 🏠 a lease's RESIDENTS (owner 2026-09-29): listed, never signing, every seat optional — seat 1 included
export const OPTIONAL_SEAT_ROLES = new Set(['resident'])
export const MAX_SEATS = 8
export const seatOf = p => (Number.isInteger(Number(p?.seat)) && Number(p.seat) >= 1 ? Number(p.seat) : 1)
/** The key a party is known by on the page: `owner` for seat 1, `owner#2` for the second. */
export const partyKey = p => (seatOf(p) > 1 ? `${p?.role}#${seatOf(p)}` : String(p?.role || ''))

/** Parties with a seat each: a stored seat is kept, a party without one takes the next free number of its role. */
export function withSeats(parties) {
  const used = new Map()
  for (const p of parties || []) if (p && Number(p.seat) >= 1) used.set(p.role, Math.max(used.get(p.role) || 0, Number(p.seat)))
  return (parties || []).map(p => {
    if (!p || Number(p.seat) >= 1) return p
    const n = (used.get(p.role) || 0) + 1
    used.set(p.role, n)
    return { ...p, seat: n }
  })
}

/** Whether another signer of this role may be added. */
export const canAddSeat = (parties, role) => !SINGLE_SEAT_ROLES.has(role) && (parties || []).filter(p => p?.role === role).length < MAX_SEATS

/** A new empty party of `role`, placed after the last one of that role (or, for the first of it, after
 *  the last `after` party — a resident goes under the tenant), on the next free seat. */
export function addSeat(parties, role, { after = null } = {}) {
  if (!canAddSeat(parties, role)) return parties
  const list = withSeats(parties)
  let at = -1
  list.forEach((p, i) => { if (p?.role === role) at = i })
  if (at < 0 && after) list.forEach((p, i) => { if (p?.role === after) at = i })
  if (at < 0 && OPTIONAL_SEAT_ROLES.has(role)) at = list.length - 1
  const seat = Math.max(0, ...list.filter(p => p?.role === role).map(seatOf)) + 1
  const blank = { role, seat, required: !OPTIONAL_SEAT_ROLES.has(role), kind: 'person', legal_name: '', names: { ka: '', en: '', ru: '' }, id_kind: null, id_number: '',
    rep_name: '', rep_position: '', phone: '', email: '', share_phone: true, share_email: false, preferred_language: null, party_id: null, source: 'added' }
  list.splice(at + 1, 0, blank)
  return list
}

/** Removes a signer of seat 2+ (the first seat of a role is the role itself and stays). */
export function removeSeat(parties, key) {
  const target = (parties || []).find(p => partyKey(p) === key)
  if (!target || (seatOf(target) === 1 && !OPTIONAL_SEAT_ROLES.has(target.role))) return parties
  return (parties || []).filter(p => p !== target)
}

// 🏠 RESIDENTS (owner 2026-09-29). A resident block nobody wrote anything in is not a resident — it is
// dropped before sending, so an opened-then-abandoned "add person 2" never reaches the contract.
export const residentEmpty = p => p?.role === 'resident' && !['legal_name', 'id_number', 'phone', 'email'].some(k => String(p?.[k] || '').trim())
/** The parties a create/save sends: every party, minus residents left completely empty. */
export const partiesToSend = parties => (parties || []).filter(p => !residentEmpty(p))
/** How many more residents this lease admits: it states the number who will live there (the tenant is
 *  person 1), so N−1 residents; 0 on a contract without the number.
 *  ⚠️ A number NOT WRITTEN YET does not hide the button (owner 2026-09-29, "I can't see where I can add
 *  it from"): the number sits below the parties in the form, so an agent filling it top to bottom never
 *  saw the button. Until it is written, residents may be added up to the seat cap; once written, it
 *  bounds them (and the gate says so if it is lowered under the residents already listed). */
export function residentRoom(fields, values, parties) {
  if (!(fields || []).some(f => f?.key === 'max_residents')) return 0
  const had = (parties || []).filter(p => p?.role === 'resident').length
  const raw = values?.max_residents
  if (raw == null || String(raw).trim() === '') return Math.max(0, MAX_SEATS - had)
  const max = Number(raw)
  if (!Number.isFinite(max) || max < 2) return 0
  return Math.max(0, Math.min(max - 1, MAX_SEATS) - had)
}
